import {
  DragHandlerWrapper,
  RowSelectButtonContainer,
} from '@databases/context-menu/styles';
import { ActiveVerticalDragHandlerIcon, VerticalDragHandlerIcon } from '@databases/icons';
import { messages } from '@databases/intl/messages';
import { useDatabaseSelectionPing } from '@databases/ping-registry';
import { type HoverProps } from '@databases/react';
import { IGNORED_TABLE_ELEMENT_CLASS } from '@databases/table-resizer';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import React, { type FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

type RowDragHandleProps = {
  entryId: string;
  hoverProps?: HoverProps;
  onSelectRow: (e: React.MouseEvent | React.KeyboardEvent) => void;
};

const RowDragHandle: FunctionComponent<RowDragHandleProps> = ({
  entryId,
  hoverProps,
  onSelectRow,
}) => {
  const { formatMessage } = useIntl();

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key !== 'Enter' && e.key !== ' ') {
        return;
      }

      e.preventDefault();
      onSelectRow(e);
    },
    [onSelectRow]
  );

  //for now we leverage the ping registry but we will look into a more ideal solution
  const isActive = useDatabaseSelectionPing((reader) => {
    const focused = reader.getFocusedItem();

    if (focused?.type !== 'entry' || focused.entryId !== entryId) {
      return false;
    }
    //Dont show the handle as active if select all
    return reader.hasEntryBeforeSelection() || reader.hasEntryAfterSelection();
  });

  return (
    <RowSelectButtonContainer className={IGNORED_TABLE_ELEMENT_CLASS} {...hoverProps}>
      <DragHandlerWrapper
        type="button"
        className={TABLE_CLASSNAMES.SELECT_BUTTON}
        data-testid="row-select-button"
        data-interactive
        isActive={isActive}
        isDeletionActive={false}
        aria-label={formatMessage(messages['table-layout.select-row-label'])}
        onKeyDown={handleKeyDown}
      >
        <div className={TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON}>
          <ActiveVerticalDragHandlerIcon
            label={TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON}
          />
        </div>
        <div className={TABLE_CLASSNAMES.DRAG_HANDLER_ICON}>
          <VerticalDragHandlerIcon label={TABLE_CLASSNAMES.DRAG_HANDLER_ICON} />
        </div>
      </DragHandlerWrapper>
    </RowSelectButtonContainer>
  );
};

export default RowDragHandle;
